import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  confirmRestaurantSpecialProposal,
  getRestaurantKey,
  submitRestaurantSpecialProposal,
} from '../utils/restaurantSpecials';

const TrustBowlMeter = ({ count, threshold }) => {
  const pct = Math.min(100, Math.round((count / threshold) * 100));
  return (
    <div className="map-trust-bowl-meter" aria-label={`${count} of ${threshold} confirms`}>
      <div className="map-trust-bowl-track">
        <div className="map-trust-bowl-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="map-trust-bowl-count">{count}/{threshold} confirms</span>
    </div>
  );
};

const RestaurantSpecialTrustBowl = ({
  item,
  auth,
  proposals = [],
  confirmThreshold = 3,
  onProposalSaved,
  onProposalConfirmed,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({
    special: '',
    specialinfo: '',
    price: '',
  });
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setStatus('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!auth?.accessToken) return;

    setSaving(true);
    setStatus('');

    try {
      const response = await submitRestaurantSpecialProposal({
        restaurantKey: getRestaurantKey(item),
        restaurantName: item.name,
        address: item.address || '',
        ...values,
      }, auth.accessToken);

      onProposalSaved?.(response.data.proposal);
      setStatus(response.data.message || 'Special added to the trust bowl.');
      setValues({ special: '', specialinfo: '', price: '' });
    } catch (err) {
      setStatus(err?.response?.data?.message || 'Could not submit special. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async (proposalId) => {
    if (!auth?.accessToken) return;

    setConfirmingId(proposalId);
    setStatus('');

    try {
      const response = await confirmRestaurantSpecialProposal(proposalId, auth.accessToken);
      onProposalConfirmed?.(response.data);
      setStatus(response.data.message || 'Thanks for confirming!');
    } catch (err) {
      setStatus(err?.response?.data?.message || 'Could not confirm special. Try again.');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div className={`map-trust-bowl${compact ? ' map-trust-bowl--compact' : ''}`}>
      <button
        type="button"
        className="map-trust-bowl-toggle"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <i className="bi bi-basket" />
        {open ? 'Hide trust bowl' : 'Trust bowl — suggest a special'}
      </button>

      {open && (
        <div className="map-trust-bowl-panel">
          <p className="map-trust-bowl-intro">
            Suggest what&apos;s on special today. When {confirmThreshold} people confirm an entry, it goes live.
          </p>

          {proposals.length > 0 && (
            <ul className="map-trust-bowl-list">
              {proposals.map((proposal) => (
                <li key={proposal.id} className="map-trust-bowl-item">
                  <p className="map-trust-bowl-special">{proposal.special}</p>
                  {proposal.specialinfo && (
                    <p className="map-trust-bowl-info">{proposal.specialinfo}</p>
                  )}
                  {proposal.price && (
                    <p className="map-trust-bowl-price">{proposal.price}</p>
                  )}
                  <p className="map-trust-bowl-meta">
                    Suggested by {proposal.submittedBy}
                  </p>
                  <TrustBowlMeter
                    count={proposal.confirmCount}
                    threshold={proposal.confirmThreshold || confirmThreshold}
                  />
                  {auth?.accessToken ? (
                    proposal.isOwnProposal ? (
                      <p className="map-trust-bowl-note">Waiting for community confirms.</p>
                    ) : proposal.confirmedByUser ? (
                      <p className="map-trust-bowl-note">You confirmed this one.</p>
                    ) : (
                      <button
                        type="button"
                        className="map-trust-bowl-confirm"
                        disabled={confirmingId === proposal.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleConfirm(proposal.id);
                        }}
                      >
                        {confirmingId === proposal.id ? 'Confirming…' : 'Confirm this special'}
                      </button>
                    )
                  ) : (
                    <p className="map-trust-bowl-signin">
                      <Link to="/login" state={{ from: { pathname: '/map', search: '?view=restaurants' } }}>
                        Sign in
                      </Link>
                      {' '}to confirm.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {auth?.accessToken ? (
            <form className="map-trust-bowl-form" onSubmit={handleSubmit}>
              <label className="map-trust-bowl-field">
                <span>What&apos;s the special?</span>
                <input
                  type="text"
                  value={values.special}
                  onChange={(event) => handleChange('special', event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  maxLength={120}
                  required
                />
              </label>
              <label className="map-trust-bowl-field">
                <span>Details (optional)</span>
                <textarea
                  value={values.specialinfo}
                  onChange={(event) => handleChange('specialinfo', event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  maxLength={400}
                  rows={2}
                />
              </label>
              <label className="map-trust-bowl-field">
                <span>Price (optional)</span>
                <input
                  type="text"
                  value={values.price}
                  onChange={(event) => handleChange('price', event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  maxLength={40}
                  placeholder="$12–16"
                />
              </label>
              <button
                type="submit"
                className="map-trust-bowl-submit"
                disabled={saving}
                onClick={(event) => event.stopPropagation()}
              >
                {saving ? 'Adding…' : 'Add to trust bowl'}
              </button>
            </form>
          ) : (
            <p className="map-trust-bowl-signin">
              <Link to="/login" state={{ from: { pathname: '/map', search: '?view=restaurants' } }}>
                Sign in
              </Link>
              {' '}to suggest a special.
            </p>
          )}
        </div>
      )}

      {status && (
        <p className={`map-trust-bowl-status${status.includes('live') || status.includes('Thanks') || status.includes('trust bowl') ? ' success' : ' error'}`}>
          {status}
        </p>
      )}
    </div>
  );
};

export default RestaurantSpecialTrustBowl;